import { useContext, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import BorseContext from "../../context/borse";
import UserContext from "../../context/user";
import useBorseRequestCreateMutation from "../../hooks/mutations/useBorseRequestCreateMutation";
import useBorseRequestUpdateMutation from "../../hooks/mutations/useBorseRequestUpdateMutation";
import { emailRegex, getSameEmailRegex } from "../../utils/regex";
import Question from "../question";
import BorseDiscardRequestModal from "./borse-discard-request-modal";
import useFocusOnFirstError from "../../hooks/useFocusOnFirstError";

const BorseStep1 = ({ discardModal, setDiscardModal }) => {
  const { userProfile } = useContext(UserContext);
  const { survey, formData, labels, campaign, setFormData, currentRequest, appLang } =
    useContext(BorseContext);
  const section = useMemo(() => survey?.definition?.sections[0], [survey]);
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    trigger,
    setFocus,
    formState: { errors, touchedFields, dirtyFields },
  } = useForm({
    mode: "onTouched",
    defaultValues: useMemo(() => formData, [formData]),
  });

  useFocusOnFirstError(errors, setFocus);

  const formWatch = watch();
  const initializedFormData = useRef();

  useEffect(() => {
    if (initializedFormData.current) {
      reset(formData);
    }
  }, [reset, formData]);

  useEffect(() => {
    if (!initializedFormData.current && section?.questions && userProfile) {
      initializedFormData.current = true;
      const initializedFields = {
        [section.questions?.[0]?.id]: userProfile?.firstName,
        [section.questions?.[1]?.id]: userProfile?.lastName,
      };
      setFormData((current) => ({ ...current, ...initializedFields }));
    }
  }, [userProfile, section, setFormData, survey]);

  const { mutateAsync: createRequest, isError: isCreateError } = useBorseRequestCreateMutation();
  const { mutateAsync: updateRequest, isError } = useBorseRequestUpdateMutation();

  const onSubmit = async (values) => {
    if (currentRequest?.surveySubmission?.submissionId) {
      await updateRequest({
        survey,
        campaignId: campaign?.id,
        request: currentRequest,
        formData,
        values,
        email: values[section.questions[6].id],
        phone: values[section.questions[4].id],
        isRequestForMe:
          Number(values[section.questions[3].id]) === Number(section.questions[3].answers[0].id),
        lastCompletedStep: 0,
      });
    } else {
      await createRequest({
        survey,
        campaignId: campaign?.id,
        formData,
        values,
        email: values[section.questions[6].id],
        phone: values[section.questions[4].id],
        isRequestForMe:
          Number(values[section.questions[3].id]) === Number(section.questions[3].answers[0].id),
        lastCompletedStep: 0,
      });
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="form" key={section.id} noValidate>
        {section?.questions?.map((question, i) => (
          <Question
            question={question}
            key={question.id}
            register={register}
            error={errors[`${question.id}`]}
            touched={touchedFields[`${question.id}`]}
            isDirty={dirtyFields[`${question.id}`]}
            control={control}
            trigger={trigger}
            readOnlyMode={i < 2}
            labels={labels}
            pattern={
              i === 6
                ? emailRegex
                : i === 7
                ? getSameEmailRegex(formWatch[section.questions[6].id])
                : null
            }
            customPatternErrorMsg={
              i === 6
                ? labels?.WELFARE_NOT_VALID_EMAIL || "The inserted email is not valid"
                : i === 7
                ? labels?.WELFARE_NOT_CORRESPONDING_EMAIL ||
                  "The inserted email does not correspond"
                : null
            }
            value={formWatch[`${question.id}`]}
            attachments={survey?.attachments}
            isInline={["Multiple choice", "Radio Self Submit"].includes(question.questionType)}
            locale={appLang?.value}
            trim={[6, 7].includes(i)}
          />
        ))}
        <div className="form__buttons-wrapper form__buttons-wrapper--static">
          {(isError || isCreateError) && (
            <p className="form__error text-center">
              {labels?.WELFARE_BORSE_ERROR_SAVING_REQUEST || "Error saving requests"}
            </p>
          )}
          <div className="form__buttons form__buttons--end">
            <button className="button primary form__submit">
              {labels?.WELFARE_FORM_BTN_NEXT || "Next"}
            </button>
          </div>
        </div>
      </form>
      <BorseDiscardRequestModal
        key={!!discardModal}
        discardModal={discardModal}
        setDiscardModal={setDiscardModal}
        formWatch={formWatch}
        lastCompletedStep={-1}
      />
    </>
  );
};

export default BorseStep1;
